import { useState, useEffect, useMemo, Suspense, lazy } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Loader2, Download, FileText } from 'lucide-react'
import { OtpGate } from '../components/otp-gate'
import { QueryResponseForm, type ProviderQuery } from '../components/query-response-form'
import {
  buildMergedLetterPdfArrayBuffer,
  buildMergedLetterPdfBlob,
  buildMergedLetterDocxBlob,
  downloadBlob,
  type LetterDraft,
} from '@/features/workspace/components/dispatch/query-letter'
import '../styles/public-response.css'

const PdfViewer = lazy(() => import('@/features/workspace/components/chart/pdf-viewer').then((m) => ({ default: m.PdfViewer })))

const PUBLIC_BASE = (() => {
  const raw = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '')
  return raw.endsWith('/app') ? `${raw.slice(0, -'/app'.length)}/public` : `${raw}/public`
})()

type Stage = 'loading' | 'awaiting_otp' | 'authenticated' | 'expired' | 'not_found'

// Convert the public-response query rows into LetterDraft shape so the FE
// renderer can produce the same Physician Query letter the coder previewed
// upstream — one page per encounter, with all queries for that encounter
// stacked on the same page.
function buildDraftsFromProviderQueries(queries: ProviderQuery[]): LetterDraft[] {
  const grouped = new Map<string, ProviderQuery[]>()
  for (const q of queries) {
    const list = grouped.get(q.encounter_id) ?? []
    list.push(q)
    grouped.set(q.encounter_id, list)
  }
  const drafts: LetterDraft[] = []
  for (const [encounterId, qs] of grouped) {
    const first = qs[0]
    const queryBlocks = qs.map((q) => ({
      title: (q.title ?? '').trim(),
      body: (q.body ?? '').trim(),
      chartAnchorText: q.chart_anchor_text?.trim() || undefined,
    }))
    const queryText = queryBlocks.map((q) => {
      const title = (q.title ?? '').trim()
      const body = (q.body ?? '').trim()
      return body ? `${title}\n${body}` : title
    }).join('\n\n')
    const dosDisplay = first.date_of_service
      ? new Date(first.date_of_service).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
      : ''
    drafts.push({
      encounterId,
      claimId: first.claim_id ?? encounterId.slice(0, 8),
      dos: first.date_of_service ?? '',
      dosDisplay,
      facility: first.facility_name ?? '',
      patientName: first.patient_name ?? first.patient_last_name ?? '',
      accountNo: first.account_no ?? first.claim_id ?? '',
      surgeon: first.surgeon_name ?? '',
      queryBlocks,
      queryText,
    })
  }
  return drafts
}

export function QueryResponsePage() {
  const { token } = useParams<{ token: string }>()
  const [stage, setStage] = useState<Stage>('loading')
  const [otp, setOtp] = useState('')
  const [queries, setQueries] = useState<ProviderQuery[]>([])
  const [attemptsLeft, setAttemptsLeft] = useState(5)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!token) { setStage('not_found'); return }
    axios.get(`${PUBLIC_BASE}/query-response/${token}`)
      .then((r) => {
        if (r.data?.data?.status === 'awaiting_otp') {
          setStage('awaiting_otp')
          setAttemptsLeft(r.data.data.otp_attempts_left ?? 5)
        } else {
          setStage('expired')
        }
      })
      .catch((e) => {
        const code = e?.response?.data?.error?.code
        if (code === 'TOKEN_EXPIRED') setStage('expired')
        else setStage('not_found')
      })
  }, [token])

  async function handleVerify(submitted: string) {
    setErrorMsg(null)
    try {
      const r = await axios.post(`${PUBLIC_BASE}/query-response/${token}/verify-otp`, { otp: submitted })
      setStage('authenticated')
      setOtp(submitted)
      setQueries((r.data?.data?.queries ?? []) as ProviderQuery[])
    } catch (e) {
      const code = (axios.isAxiosError(e) && e.response?.data?.error?.code) || 'INVALID'
      if (code === 'TOKEN_EXPIRED' || code === 'EXPIRED') { setStage('expired'); return }
      if (code === 'ATTEMPTS_EXHAUSTED' || code === 'LOCKED') {
        setErrorMsg('Too many attempts — please wait 15 minutes before trying again.')
        return
      }
      setAttemptsLeft((n) => Math.max(0, n - 1))
      setErrorMsg('Incorrect access code. Please re-check the OTP email and try again.')
    }
  }

  async function handleSubmitResponse(queryId: string, outcome: ProviderQuery['outcome_choice'], freeText: string | null) {
    await axios.post(
      `${PUBLIC_BASE}/query-response/${token}/queries/${queryId}/respond`,
      { otp, outcome, free_text: freeText },
    )
    setQueries((prev) => prev.map((q) => q.id === queryId ? { ...q, status: 'recipient_responded' } : q))
  }

  return (
    <div className="public-response-shell">
      <header className="public-response-shell__header">
        <img
          alt="Medicodio"
          src="/brand/medicodio-logo-white.png"
          style={{ height: 32, width: 'auto', display: 'block' }}
        />
      </header>

      <main
        className={`public-response-shell__body${stage === 'authenticated' ? ' public-response-shell__body--split' : ''}`}
      >
        {stage === 'loading' && (
          <div className="public-response-card">
            <Loader2 size={20} className="animate-spin" /> Loading query…
          </div>
        )}

        {stage === 'not_found' && (
          <div className="public-response-card public-response-card--error">
            <h1>Link not found</h1>
            <p>This response link is invalid or has already been retired. Please contact your coding team if you believe this is an error.</p>
          </div>
        )}

        {stage === 'expired' && (
          <div className="public-response-card public-response-card--error">
            <h1>This link has expired</h1>
            <p>Please contact your coding team to receive a fresh query email.</p>
          </div>
        )}

        {stage === 'awaiting_otp' && (
          <OtpGate
            attemptsLeft={attemptsLeft}
            errorMsg={errorMsg}
            onSubmit={handleVerify}
          />
        )}

        {stage === 'authenticated' && (
          <AuthenticatedPane queries={queries} onSubmit={handleSubmitResponse} />
        )}
      </main>

      <footer className="public-response-shell__footer">
        This message contains protected health information. If you are not the intended recipient please delete it.
      </footer>
    </div>
  )
}

// Split pane: Physician Query letter PDF on the left (FE-rendered, same as
// the email attachment), compact response form on the right. Recipient sees
// the exact letter that arrived in their inbox.
function AuthenticatedPane({
  queries,
  onSubmit,
}: {
  queries: ProviderQuery[]
  onSubmit: (queryId: string, outcome: ProviderQuery['outcome_choice'], freeText: string | null) => Promise<void>
}) {
  const drafts = useMemo(() => buildDraftsFromProviderQueries(queries), [queries])
  const senderName = 'Medicodio Coding Operations'
  const pdfBuffer = useMemo(() => buildMergedLetterPdfArrayBuffer(drafts, senderName), [drafts])
  const [downloading, setDownloading] = useState<'pdf' | 'docx' | null>(null)

  async function handleDownloadPdf() {
    setDownloading('pdf')
    try {
      const blob = buildMergedLetterPdfBlob(drafts, senderName)
      await downloadBlob(blob, 'physician-query.pdf')
    } finally {
      setDownloading(null)
    }
  }

  async function handleDownloadDocx() {
    setDownloading('docx')
    try {
      const blob = await buildMergedLetterDocxBlob(drafts, senderName)
      await downloadBlob(blob, 'physician-query.docx')
    } finally {
      setDownloading(null)
    }
  }

  return (
    <>
      <section className="public-response-pdf">
        <div className="public-response-pdf__header">
          <FileText size={13} />
          <strong>Physician Query letter ({drafts.length} {drafts.length === 1 ? 'page' : 'pages'})</strong>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              type="button"
              className="public-response-pdf__btn"
              onClick={handleDownloadPdf}
              disabled={downloading !== null}
            >
              <Download size={12} /> {downloading === 'pdf' ? 'PDF…' : 'PDF'}
            </button>
            <button
              type="button"
              className="public-response-pdf__btn"
              onClick={handleDownloadDocx}
              disabled={downloading !== null}
            >
              <Download size={12} /> {downloading === 'docx' ? 'DOCX…' : 'DOCX'}
            </button>
          </span>
        </div>
        <div className="public-response-pdf__viewer">
          <Suspense fallback={<div style={{ padding: 16, fontSize: 12, color: '#94a3b8' }}>Loading PDF…</div>}>
            <PdfViewer data={pdfBuffer} />
          </Suspense>
        </div>
      </section>

      <section className="public-response-list">
        <QueryResponseForm queries={queries} onSubmit={onSubmit} />
      </section>
    </>
  )
}
